// store/auth.ts
import { type Account } from '@/types/Account';
import { API } from '@whaleden/core/api';
import { DataWrapper } from '@whaleden/core/types/DataWrapper';
import { jwtDecode } from 'jwt-decode';
import { defineStore } from 'pinia';

interface User {
    realm_access?: {
        roles: string[];
    };
    [key: string]: any;
}

interface AuthState {
    user: User | null;
    referralInvitedCount: number;
    account: DataWrapper<Account> | null;
    fetching: boolean;
}

export const useAuthStore = defineStore('auth', {
    state: (): AuthState => ({
        user: null,
        account: new DataWrapper<Account>(),
        referralInvitedCount: 0,
        fetching: false,
    }),

    actions: {
        async setIdentity(token: string, isAdmin = false) {
            try {
                this.user = token ? jwtDecode<User>(token) : null;

                if (this.user && !isAdmin && (!this.account?.fetched || this.account?.error)) {
                    this.account?.setData(await API.IdentityService.getAccount(this.user.sub as string));
                    this.referralInvitedCount = await API.IdentityService.invitedCount(this.user.sub as string);

                    if (!this.user) {
                        throw Error();
                    }
                }
            } catch (error) {
                this.account?.setError();
            }
        },
        async fetchAccount() {
            try {
                if (this.user) {
                    this.account?.setData(await API.IdentityService.getAccount(this.user.sub as string));
                }
            } catch (e) {
                this.account?.setError();
            }
        },
    },

    getters: {
        hasRole:
            (state) =>
            (role: string): boolean => {
                if (!state.user?.realm_access?.roles) return false;
                return state.user.realm_access.roles.includes(role);
            },
    },
});
